import { useState, useCallback, useEffect } from "react";

import { useNavigate } from "react-router-dom";
import {
  Bell,
  BellDot,
  CheckCheck,
  Clock,
  Wifi,
  WifiOff,
  X,
  Trash2,
} from "lucide-react";
import Swal from "sweetalert2";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ScrollArea } from "../ui/scroll-area";
import { cn } from "../../lib/utils";
import { useSocket } from "../../hooks/use-socket";
import {
  useNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useClearNotifications,
  useDeleteNotification,
} from "../../hooks/notification/useNotification";
import {
  getNotifTitle,
  getNotifBody,
  getNotifTime,
} from "../../utils/NotificationHelpers";

// type AnyNotification = NotificationItem | ApiNotificationItem;

// ─── NotifItem ────────────────────────────────────────────────────────────────

function NotifItem({ notif, isRead, onRead, onDelete }) {
  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(notif.notificationId);
  };

  return (
    <div
      className={cn(
        "group relative flex gap-3 px-4 py-3 transition-colors hover:bg-muted/50 cursor-pointer border-b border-border/40 last:border-0",
        !isRead && "bg-primary/5",
      )}
      onClick={onRead}
    >
      {/* Unread dot */}
      {!isRead && (
        <span className="absolute left-2 top-4 h-1.5 w-1.5 rounded-full bg-primary" />
      )}

      {/* Icon */}
      <div
        className={cn(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          isRead ? "bg-muted" : "bg-primary/10",
        )}
      >
        <Bell
          className={cn(
            "h-4 w-4",
            isRead ? "text-muted-foreground" : "text-primary",
          )}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-sm leading-snug line-clamp-3",
            isRead
              ? "text-muted-foreground font-normal"
              : "text-foreground font-medium",
          )}
        >
          {notif.standard_name && (
            <span className="text-primary font-semibold">
              {notif.standard_name}
            </span>
          )}
          {notif.standard_name && notif.module_name && (
            <span className="mx-1 text-muted-foreground/50">•</span>
          )}
          <span
            className={cn(
              "font-semibold",
              isRead ? "text-muted-foreground" : "text-[#4A1E6A]",
            )}
          >
            {getNotifTitle(notif)}
          </span>
          {/* <span className="mx-1 text-muted-foreground/70">:</span> */}
          <span className="text-xs text-muted-foreground/90 font-normal ms-1">
            {getNotifBody(notif)}
          </span>
        </p>
        <div className="flex items-center gap-1 pt-0.5 text-[10px] text-muted-foreground/70">
          <Clock className="h-2.5 w-2.5" />
          <span>{getNotifTime(notif)}</span>
        </div>
      </div>

      {/* Delete Button */}
      {onDelete && (
        <button
          onClick={handleDelete}
          className="absolute right-2 bottom-2 h-7 w-7 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
          title="Delete alert"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ─── NotificationPanel ────────────────────────────────────────────────────────

export function NotificationPanel() {
  const { notifications, isConnected, setNotifications } = useSocket();
  const [open, setOpen] = useState(false);

  const { data: notificationData } = useNotification();
  const { mutate: markAllAsReadApi } = useMarkAllNotificationsRead();
  const { mutate: markAsReadApi } = useMarkNotificationRead();
  const { mutate: clearAllApi } = useClearNotifications();
  const { mutate: deleteNotificationApi } = useDeleteNotification();
  const navigate = useNavigate();

  // Mapping of modules to their parent route segments in metascroll
  const routesMap = {
    // Activity modules
    "implementation-checklist": "activity",
    "master-list-of-documents": "activity",
    "audit-schedule": "activity",
    "internal-audit-checklist": "activity",
    "ciso-mom": "activity",
    maintenance: "activity",
    // Information modules
    "standard-information": "information",
    "sla-management": "information",
    "task-management": "information",
    "vendor-management": "information",
    "security-incident": "information",
    "training-calendar": "information",
    "calendar-management": "information",
    // Reference Doc modules
    "reference-doc-format": "reference-doc",
    "website-tracking": "reference-doc",
    "consent-management": "reference-doc",
    "certification-tracking": "reference-doc",
    "statutory-reporting": "reference-doc",
    "cookie-management": "reference-doc",
    "glossary-faq": "reference-doc",
    "ie-audit-reports": "reference-doc",
    "standard-comparison": "reference-doc",
    diagramsandlogos: "reference-doc",
    reference: "reference-doc", // Special case for reference files
  };

  const handleNavigate = (n) => {
    const { module_name, standard_id, id, row_id, folder_id } = n;
    if (!module_name || !standard_id) return;

    console.log("module_name", module_name);
    console.log("standard_id", standard_id);
    console.log("row_id", row_id);
    console.log("folder_id", folder_id);

    // 👉 Handle special cases first
    if (module_name === "review-and-approved") {
      navigate(`/standard/${standard_id}/settings/approval-status`, {
        state: { openItemViewId: row_id },
      });
      setOpen(false);
      return;
    }

    if (module_name === "manual-status-update") {
      navigate(`/standard/${standard_id}/settings/manual-status`, {
        state: { openItemViewId: row_id },
      });
      setOpen(false);
      return;
    }

    // 👉 Existing logic
    const parent = routesMap[module_name];
    if (parent) {
      if (module_name === "reference") {
        if (folder_id) {
          navigate(
            `/standard/${standard_id}/reference-doc/reference/${folder_id}`,
            {
              state: { openItemViewId: row_id },
            },
          );
        } else {
          navigate(`/standard/${standard_id}/reference-doc/reference`, {
            state: { openItemViewId: row_id },
          });
        }
      } else {
        navigate(`/standard/${standard_id}/${parent}/${module_name}`, {
          state: { openItemViewId: row_id },
        });
      }

      setOpen(false);
    }
  };

  // Clear socket notifications when API data updates, as API contains the latest state
  useEffect(() => {
    setNotifications([]);
  }, [notificationData, setNotifications]);

  const handleOpenChange = (state) => {
    setOpen(state);
  };
  const socketUnreadCount = notifications.filter((n) => !n.is_read).length;
  const apiUnreadCount =
    notificationData?.data?.notifications?.filter((n) => !n.is_read).length ||
    0;
  const unreadCount = socketUnreadCount + apiUnreadCount;

  console.log("notifications", notifications);
  console.log("notificationData", notificationData);

  const markAllRead = useCallback(() => {
    markAllAsReadApi();
  }, [markAllAsReadApi]);

  const clearAll = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to clear all alerts?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#7030A0",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, clear all",
    }).then((result) => {
      if (result.isConfirmed) {
        clearAllApi();
      }
    });
  };

  const hasUnread = unreadCount > 0;
  const hasNotifications =
    notifications.length +
      (notificationData?.data?.notifications?.length || 0) >
    0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 rounded-full"
          aria-label="Alerts"
        >
          {hasUnread ? (
            <BellDot className="h-5 w-5 text-white" />
          ) : (
            <Bell className="h-5 w-5 text-white" />
          )}
          {hasUnread && (
            <span className="absolute -right-0 -top-0 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground ring-2 ring-primary">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[380px] p-0 shadow-xl rounded-xl border-border bg-popover"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-3 border-b border-border/60 ">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold">Alerts</h2>
            {isConnected ? (
              <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-500">
                <Wifi className="h-2.5 w-2.5" /> Live
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                <WifiOff className="h-2.5 w-2.5" /> Offline
              </span>
            )}
          </div>

          <div className="flex items-center gap-0.5">
            {hasUnread && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-1.5 gap-1 text-[10px] text-muted-foreground hover:text-foreground"
                onClick={markAllRead}
              >
                <CheckCheck className="size-3" />
                Mark all read
              </Button>
            )}
            {hasNotifications && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-1.5 gap-1 text-[10px] text-destructive hover:text-destructive/80"
                onClick={clearAll}
              >
                <Trash2 className=" size-3" />
                Clear all
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setOpen(false)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Body */}
        {notifications.length === 0 &&
        notificationData?.data?.notifications?.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <Bell className="h-6 w-6 opacity-40" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">No alerts yet</p>
              <p className="text-xs text-muted-foreground/70 mt-0.5">
                We'll alert you when something arrives
              </p>
            </div>
          </div>
        ) : (
          <>
            <ScrollArea className="h-[400px]">
              {/* <div className="bg-amber-400"> */}
              {notifications.map((n) => (
                <NotifItem
                  key={n.notificationId}
                  notif={n}
                  isRead={n.is_read}
                  onRead={() => {
                    markAsReadApi(n?.notificationId);
                    handleNavigate(n);
                  }}
                  onDelete={(id) => deleteNotificationApi(id)}
                />
              ))}
              {/* </div> */}
              {notificationData?.data?.notifications?.map((n) => (
                <NotifItem
                  key={n.notificationId}
                  notif={n}
                  isRead={n.is_read}
                  onRead={() => {
                    markAsReadApi(n.notificationId);
                    handleNavigate(n);
                  }}
                  onDelete={(id) => deleteNotificationApi(id)}
                />
              ))}
            </ScrollArea>

            {/* Footer */}
            <div className="border-t border-border/60 px-4 py-2.5 text-center">
              <p className="text-xs text-muted-foreground">
                {notifications.length} alert
                {notifications.length !== 1 ? "s" : ""} •{" "}
                {unreadCount > 0 ? `${unreadCount} unread` : "all read"}
              </p>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
